import React, { useState, useEffect } from 'react';

const CywareIntelDashboard = () => {
    const [intelData, setIntelData] = useState([]);
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedCategory, setSelectedCategory] = useState('all');
    const [selectedUrgency, setSelectedUrgency] = useState('all');
    const [selectedTLP, setSelectedTLP] = useState('all');

    useEffect(() => {
        loadCywareIntel();
        loadCategories();
    }, [selectedCategory, selectedUrgency, selectedTLP]);

    const loadCywareIntel = async () => {
        try {
            setLoading(true);
            
            // Build query with filters
            let queryFilters = [];
            if (selectedCategory !== 'all') queryFilters.push(`category: "${selectedCategory}"`);
            if (selectedUrgency !== 'all') queryFilters.push(`urgency: "${selectedUrgency}"`);
            if (selectedTLP !== 'all') queryFilters.push(`tlp: "${selectedTLP}"`);
            
            const filterString = queryFilters.length > 0 ? `(${queryFilters.join(', ')}, limit: 50)` : '(limit: 50)';

            const response = await fetch('/api/graphql', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    query: `{
                        cywareIntel${filterString} {
                            intel_id
                            title
                            description
                            category_name
                            tlp
                            urgency
                            confidence
                            credibility
                            created_at
                            imported_at
                            indicators
                            cves
                            techniques
                            recommendation
                        }
                    }`
                })
            });

            if (response.ok) {
                const data = await response.json();
                if (data.data && data.data.cywareIntel) {
                    setIntelData(data.data.cywareIntel);
                }
            }
        } catch (error) {
            console.error('Error loading Cyware intel:', error);
        } finally {
            setLoading(false);
        }
    };

    const loadCategories = async () => {
        try {
            const response = await fetch('/api/graphql', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    query: `{
                        intelCategories {
                            category_id
                            name
                            intel_count
                        }
                    }`
                })
            });

            if (response.ok) {
                const data = await response.json();
                if (data.data && data.data.intelCategories) {
                    setCategories(data.data.intelCategories);
                }
            }
        } catch (error) {
            console.error('Error loading categories:', error);
        }
    };

    const getTLPColor = (tlp) => {
        const colors = {
            'RED': 'danger',
            'AMBER+STRICT': 'warning',
            'AMBER': 'warning',
            'GREEN': 'success',
            'CLEAR': 'info'
        };
        return colors[tlp] || 'secondary';
    };

    const getUrgencyIcon = (urgency) => {
        const icons = {
            'Urgent': '🚨',
            'Elevated': '⚠️',
            'Normal': 'ℹ️',
            'Low': '📊'
        };
        return icons[urgency] || '📋';
    };

    const getConfidenceStars = (confidence) => {
        const stars = {
            'Very High': '⭐⭐⭐⭐⭐',
            'High': '⭐⭐⭐⭐',
            'Medium': '⭐⭐⭐',
            'Low': '⭐⭐',
            'Very Low': '⭐'
        };
        return stars[confidence] || '⭐⭐⭐';
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        return date.toLocaleString();
    };

    return (
        <div className="cyware-intel-dashboard">
            {/* Header & Statistics */}
            <div className="card mb-4">
                <div className="card-header bg-primary text-white">
                    <h5 className="mb-0">
                        <i className="fas fa-shield-alt me-2"></i>
                        Cyware S-ISAC Threat Intelligence
                    </h5>
                </div>
                <div className="card-body">
                    <div className="row">
                        {categories.map(cat => (
                            <div key={cat.category_id} className="col-md-4 col-sm-6 mb-2">
                                <div className="small">
                                    <strong>{cat.name}:</strong> <span className="badge bg-secondary">{cat.intel_count}</span>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* Filters */}
            <div className="card mb-4">
                <div className="card-body">
                    <div className="row">
                        <div className="col-md-4">
                            <label className="form-label small">Category</label>
                            <select 
                                className="form-select form-select-sm"
                                value={selectedCategory}
                                onChange={(e) => setSelectedCategory(e.target.value)}
                            >
                                <option value="all">All Categories</option>
                                {categories.map(cat => (
                                    <option key={cat.category_id} value={cat.name}>
                                        {cat.name} ({cat.intel_count})
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div className="col-md-4">
                            <label className="form-label small">Urgency</label>
                            <select 
                                className="form-select form-select-sm"
                                value={selectedUrgency}
                                onChange={(e) => setSelectedUrgency(e.target.value)}
                            >
                                <option value="all">All Urgencies</option>
                                <option value="Urgent">🚨 Urgent</option>
                                <option value="Elevated">⚠️ Elevated</option>
                                <option value="Normal">ℹ️ Normal</option>
                                <option value="Low">📊 Low</option>
                            </select>
                        </div>
                        <div className="col-md-4">
                            <label className="form-label small">TLP Level</label>
                            <select 
                                className="form-select form-select-sm"
                                value={selectedTLP}
                                onChange={(e) => setSelectedTLP(e.target.value)}
                            >
                                <option value="all">All TLP Levels</option>
                                <option value="RED">🔴 RED</option>
                                <option value="AMBER+STRICT">🟠 AMBER+STRICT</option>
                                <option value="AMBER">🟡 AMBER</option>
                                <option value="GREEN">🟢 GREEN</option>
                                <option value="CLEAR">⚪ CLEAR</option>
                            </select>
                        </div>
                    </div>
                </div>
            </div>

            {/* Intel Cards */}
            {loading ? (
                <div className="text-center p-5">
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                </div>
            ) : intelData.length === 0 ? (
                <div className="alert alert-info">
                    <i className="fas fa-info-circle me-2"></i>
                    No intel available for selected filters.
                </div>
            ) : (
                <div className="intel-list">
                    {intelData.map(intel => (
                        <div key={intel.intel_id} className="card mb-3">
                            <div className={`card-header bg-${getTLPColor(intel.tlp)} bg-opacity-10 border-${getTLPColor(intel.tlp)}`}>
                                <div className="d-flex justify-content-between align-items-start">
                                    <div className="flex-grow-1">
                                        <h6 className="mb-1">
                                            {getUrgencyIcon(intel.urgency)} {intel.title}
                                        </h6>
                                        <div className="small text-muted">
                                            <span className="me-3">
                                                <i className="fas fa-tag me-1"></i>
                                                {intel.category_name}
                                            </span>
                                            <span className="me-3">
                                                <span className={`badge bg-${getTLPColor(intel.tlp)}`}>
                                                    TLP: {intel.tlp}
                                                </span>
                                            </span>
                                            <span className="me-3">
                                                {getConfidenceStars(intel.confidence)} {intel.confidence}
                                            </span>
                                            <span className="me-3">
                                                <i className="fas fa-clock me-1"></i>
                                                {formatDate(intel.created_at)}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default CywareIntelDashboard;
